import {useFormik} from 'formik';
import {Box, Stack, Typography} from '@mui/material';
import BookingStepper from '../components/BookingStepper';
import Footer from '../components/Footer';
import {
  BookingDataUpdater,
  BookingStepperStage,
  BookingView,
  PatientInsuranceDetailViewChoices,
} from '../types';
import styles, {patientInsuranceDetailViewStyles} from '../styles';
import {
  PatientInsuranceDetailFormData,
  patientInsuranceDetailFormSchema,
} from '../components/PatientInsuranceDetailForm/schema';
import PatientInsuranceDetailForm from '../components/PatientInsuranceDetailForm';
import AppointmentSummaryTile from '../components/AppointmentSummaryTile';
import {
  useAppointment,
  useProcessCardPayment,
  useProcessPayPalPayment,
  useStripeClientSecret,
} from '../hooks';
import {Elements} from '@stripe/react-stripe-js';
import {stripePromise} from '../config/strip';
import {useRef, useState, useEffect} from 'react';
import {StripeWidgetRef} from '../components/StripeWidget';
import {CircularProgress} from '@mui/material';
import {bookingChoicesToAppointmentRequestBody} from '../utils';
import {
  ERROR_MESSAGES,
  PAYMENT_PROVIDER,
  BUTTON_TEXT,
  DEFAULT_VALUES,
  INSURANCE_DETAIL_VIEW_TEXT,
} from '../constant';
import {ToastType} from '$components/Toast/constant';
import toast from '$utils/toast';
import {networkErrorToast} from '$constants/toastmessages';
import {handleBeforeUnload} from '../utils';
import LocalStorage from '~/utils/localStore';

interface PatientInsuranceDetailViewProps {
  bookingChoices: PatientInsuranceDetailViewChoices;
  setActiveView: (view: BookingView) => void;
  setBookingChoices: BookingDataUpdater;
}

export default function PatientInsuranceDetailView(
  props: PatientInsuranceDetailViewProps
) {
  const {bookingChoices, setActiveView, setBookingChoices} = props;

  const [isPaymentButtonClicked, setIsPaymentButtonClicked] =
    useState<boolean>(false);

  const stripeWidgetRef = useRef<StripeWidgetRef>(null);

  const {disableSubmit, isPending, saveAppointment} = useAppointment(
    bookingChoices,
    setActiveView,
    setBookingChoices
  );
  const {processCardPayment, isProcessingPayment} =
    useProcessCardPayment(setActiveView);

  const {processPayPalPayment, isProcessingPayPal} = useProcessPayPalPayment();

  const [isPayPalLoading, setIsPayPalLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const {createPaymentIntent} = useStripeClientSecret(setClientSecret);
  const [shouldCreatePaymentIntent, setShouldCreatePaymentIntent] =
    useState(false);

  const handleSubmit = (values: PatientInsuranceDetailFormData) => {
    setBookingChoices(draft => {
      draft.patientInsuranceDetail = values;
    });

    saveAppointment({patientInsuranceDetail: values});
  };

  useEffect(() => {
    if (!shouldCreatePaymentIntent) return;
    const consultationFee =
      bookingChoices.consultant?.priceLists?.consultationFee ||
      DEFAULT_VALUES.CONSULTATION_FEE;

    const amount = consultationFee;
    const currency = 'gbp';

    createPaymentIntent({amount, currency});

    setBookingChoices(draft => {
      draft.consultationAmount = consultationFee;
    });

    setShouldCreatePaymentIntent(false);
  }, [
    shouldCreatePaymentIntent,
    bookingChoices.consultant?.priceLists?.consultationFee,
    createPaymentIntent,
    setBookingChoices,
  ]);

  const formik = useFormik<PatientInsuranceDetailFormData>({
    initialValues: {
      ...(bookingChoices.patientInsuranceDetail || {
        selfPaying: null as unknown as NonNullable<boolean | null | undefined>,
        insuranceId: NaN,
        paymentMode: DEFAULT_VALUES.EMPTY_STRING,
        membershipNumber: DEFAULT_VALUES.EMPTY_STRING,
        preAuthCode: DEFAULT_VALUES.EMPTY_STRING,
        acceptResponsibility: false,
      }),
      consultant: bookingChoices.consultant,
    },
    validationSchema: patientInsuranceDetailFormSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (formik.values.selfPaying === true) {
      setShouldCreatePaymentIntent(true);
    }
  }, [formik.values.selfPaying]);

  const handleBackClick = () => {
    if (!bookingChoices.registered?.isRegistered) {
      setActiveView(BookingView.LastStepView);
    } else {
      setActiveView(BookingView.PatientContactDetailView);
    }
  };

  const handlePaymentButtonClick = () => {
    setIsPaymentButtonClicked(true);
    handlePaymentContinueClick();
  };

  const handlePaymentContinueClick = async () => {
    if (formik.values.selfPaying === null) {
      return;
    }

    setBookingChoices(draft => {
      draft.patientInsuranceDetail = formik.values;

      if (formik.values.selfPaying === true) {
        draft.insurance_claim = undefined;
      } else {
        draft.payment = undefined;
      }
    });

    if (formik.values.selfPaying === true) {
      try {
        if (!stripeWidgetRef.current) {
          toast(
            ToastType.Error,
            'Payment Error',
            ERROR_MESSAGES.PAYMENT_FORM_NOT_INITIALIZED
          );

          setIsPaymentButtonClicked(false);
          return;
        }

        const paymentMethod =
          await stripeWidgetRef.current.createPaymentMethod();
        if (!paymentMethod) {
          toast(
            ToastType.Error,
            'Payment Error',
            ERROR_MESSAGES.PAYMENT_METHOD_FAILED
          );

          setIsPaymentButtonClicked(false);
          return;
        }

        const consultationFee =
          bookingChoices.consultant?.doctorDetails?.consultationFee ||
          DEFAULT_VALUES.CONSULTATION_FEE;

        setBookingChoices(draft => {
          draft.consultationAmount = consultationFee;
        });

        const response = await processCardPayment({
          payment: {
            patient_id:
              bookingChoices.registered?.patientId || DEFAULT_VALUES.PATIENT_ID,
            consultant_id:
              bookingChoices.consultant?.id || DEFAULT_VALUES.CONSULTANT_ID,
            payment_method_id: paymentMethod.id,
            provider: PAYMENT_PROVIDER.STRIPE,
          },
          appointment: bookingChoicesToAppointmentRequestBody({
            ...bookingChoices,
            patientInsuranceDetail: formik.values,
          }).appointment,
        });

        if (!response?.data) {
          setIsPaymentButtonClicked(false);
          setActiveView(BookingView.PaymentFailureView);
          return;
        }

        const consultationPaymentId = response.data.appointment.payment.id;

        if (response?.success && consultationPaymentId) {
          setBookingChoices(draft => {
            draft.payment_id = consultationPaymentId;
          });
          setActiveView(BookingView.AppSummaryView);
        } else {
          setIsPaymentButtonClicked(false);
          setActiveView(BookingView.PaymentFailureView);
        }
      } catch (error) {
        if (error instanceof Error && error.message === 'Network Error') {
          networkErrorToast();
        } else {
          toast(
            ToastType.Error,
            'Payment Error',
            ERROR_MESSAGES.PAYMENT_PROCESSING_FAILED
          );
        }
        setIsPaymentButtonClicked(false);

        console.error('Payment processing failed:', error);
      }
    } else if (formik.values.selfPaying === false) {
      setBookingChoices(draft => {
        if (formik.values.insuranceId) {
          draft.insurance_claim = {
            insurance_id: formik.values.insuranceId,
            membership_no: formik.values.membershipNumber,
            pre_authorisation_code:
              formik.values.preAuthCode || DEFAULT_VALUES.EMPTY_STRING,
          };

          if (draft.consultant?.doctorDetails?.consultationFee) {
            draft.consultationAmount =
              draft.consultant.doctorDetails.consultationFee;
          }
        }
      });

      if (!bookingChoices.registered?.isRegistered) {
        await saveAppointment({patientInsuranceDetail: formik.values});
      } else {
        formik.submitForm();
      }
    }
  };

  const getContinueButtonText = () => {
    if (formik.values.selfPaying === null) {
      return BUTTON_TEXT.SELECT_PAYMENT_OPTION;
    }
    return formik.values.selfPaying === true
      ? BUTTON_TEXT.PAY_AND_SUBMIT
      : BUTTON_TEXT.SUBMIT_REQUEST;
  };

  const handlePayPalClick = async () => {
    setIsPayPalLoading(true);
    window.removeEventListener('beforeunload', handleBeforeUnload);

    // Save bookingChoices and current step to localStorage before redirecting
    LocalStorage.saveBookingChoices(
      bookingChoices as unknown as Record<string, unknown>
    );
    LocalStorage.saveActiveView('PatientInsuranceDetailView');

    const response = await processPayPalPayment({
      payment: {
        patient_id:
          bookingChoices.registered?.patientId || DEFAULT_VALUES.PATIENT_ID,
        consultant_id:
          bookingChoices.consultant?.id || DEFAULT_VALUES.CONSULTANT_ID,
        payment_method_id: '',
        provider: PAYMENT_PROVIDER.PAYPAL,
      },
      appointment: bookingChoicesToAppointmentRequestBody({
        ...bookingChoices,
        patientInsuranceDetail: formik.values,
      }).appointment,
    });

    if (response.success && response.data && response.data.approve_url) {
      window.location.href = response.data.approve_url;
      return;
    }
    toast(
      ToastType.Error,
      'Payment Error',
      ERROR_MESSAGES.PAYMENT_PROCESSING_FAILED
    );
    setIsPayPalLoading(false);
  };

  if (formik.values.selfPaying === true && !clientSecret) {
    return (
      <Box sx={patientInsuranceDetailViewStyles.loadingContainer}>
        <CircularProgress
          size={DEFAULT_VALUES.CIRCLE_PROGRESS_SIZE}
          color="primary"
        />
        <Typography variant="body2"></Typography>
      </Box>
    );
  }

  if (isProcessingPayment || isProcessingPayPal || isPayPalLoading) {
    return (
      <Box sx={patientInsuranceDetailViewStyles.loadingContainer}>
        <CircularProgress
          size={DEFAULT_VALUES.CIRCLE_PROGRESS_SIZE}
          color="primary"
        />
      </Box>
    );
  }

  const isFormValid = () => {
    if (formik.values.selfPaying === null) {
      return false;
    }

    if (formik.values.selfPaying === true) {
      return !disableSubmit;
    } else {
      return (
        !disableSubmit &&
        formik.values.insuranceId &&
        formik.values.membershipNumber &&
        formik.values.acceptResponsibility
      );
    }
  };

  return (
    <Box
      sx={styles.sectionWrapperWithStepper}
      flexDirection="column"
      gap={DEFAULT_VALUES.FLEX_GAP}
    >
      <Stack
        gap={DEFAULT_VALUES.STACK_GAP}
        sx={patientInsuranceDetailViewStyles.container}
      >
        <Box>
          <BookingStepper activeStage={BookingStepperStage.Insurance} />
        </Box>
        <Stack gap={DEFAULT_VALUES.INNER_STACK_GAP}>
          <Typography variant="h2" sx={styles.subHeading}>
            {formik.values.selfPaying === null
              ? INSURANCE_DETAIL_VIEW_TEXT.PAYMENT_DETAILS
              : formik.values.selfPaying
                ? INSURANCE_DETAIL_VIEW_TEXT.PAYMENT_METHOD
                : INSURANCE_DETAIL_VIEW_TEXT.INSURANCE_DETAILS}
          </Typography>
          <Typography variant="body1">
            {formik.values.selfPaying === null
              ? INSURANCE_DETAIL_VIEW_TEXT.FILL_DETAILS
              : formik.values.selfPaying
                ? INSURANCE_DETAIL_VIEW_TEXT.SELECT_PAYMENT
                : INSURANCE_DETAIL_VIEW_TEXT.FILL_DETAILS}
          </Typography>
        </Stack>
        {formik.values.selfPaying === true ? (
          clientSecret ? (
            <Elements stripe={stripePromise()} options={{clientSecret}}>
              <PatientInsuranceDetailForm
                values={formik.values}
                errors={formik.errors}
                touched={formik.touched}
                handleBlur={formik.handleBlur}
                handleChange={formik.handleChange}
                setFieldTouched={formik.setFieldTouched}
                setFieldValue={formik.setFieldValue}
                setValues={formik.setValues}
                stripeWidgetRef={stripeWidgetRef}
                onPayPalClick={handlePayPalClick}
                bookingChoices={bookingChoices}
              />
            </Elements>
          ) : (
            <Box sx={patientInsuranceDetailViewStyles.loadingContainer}>
              <CircularProgress
                size={DEFAULT_VALUES.CIRCLE_PROGRESS_SIZE}
                color="primary"
              />
              <Typography variant="body2">Loading payment form...</Typography>
            </Box>
          )
        ) : (
          // For non-self-paying (or not yet selected), show the form without <Elements>
          <PatientInsuranceDetailForm
            values={formik.values}
            errors={formik.errors}
            touched={formik.touched}
            handleBlur={formik.handleBlur}
            handleChange={formik.handleChange}
            setFieldTouched={formik.setFieldTouched}
            setFieldValue={formik.setFieldValue}
            setValues={formik.setValues}
            stripeWidgetRef={stripeWidgetRef}
            onPayPalClick={handlePayPalClick}
            bookingChoices={bookingChoices}
          />
        )}

        <Box sx={patientInsuranceDetailViewStyles.summaryContainer}>
          <AppointmentSummaryTile choices={bookingChoices} />
          <Footer
            disableContinue={!isFormValid() || isPaymentButtonClicked}
            continueButtonLabel={getContinueButtonText()}
            onBackClick={handleBackClick}
            continueLoading={isPending}
            onContinueClick={handlePaymentButtonClick}
          />
        </Box>
      </Stack>
    </Box>
  );
}
